import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useFirestoreCollection } from "@/hooks/useFirestoreCollection";
import { toArray } from "@/lib/format";
import LiveEventCard from "@/components/LiveEventCard";

const BG =
  "https://images.unsplash.com/photo-1558620013-a08999547a36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHx1cmJhbiUyMHN0YWdlJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzkxNzU4OTk1fDA&ixlib=rb-4.1.0&q=85";

export default function LiveEventsPreview() {
  const { data: events } = useFirestoreCollection("eventos");
  const { data: torneos } = useFirestoreCollection("torneos");

  const namesByEventId = {};
  (torneos || []).forEach((t) => {
    const key = t.eventId || t.eventoId;
    if (!key) return;
    if (!namesByEventId[key]) namesByEventId[key] = new Set();
    toArray(t.listaNombres).forEach((n) =>
      namesByEventId[key].add(typeof n === "string" ? n : n?.nombre || "")
    );
  });

  const getCount = (ev) => {
    const names = namesByEventId[ev.id];
    if (names && names.size > 0) return names.size;
    return toArray(ev.participantes || ev.listaNombres).length;
  };

  const sorted = [...(events || [])].sort((a, b) => {
    const la = a.isClosed ? 1 : 0;
    const lb = b.isClosed ? 1 : 0;
    if (la !== lb) return la - lb;
    return (b.fecha?.seconds || 0) - (a.fecha?.seconds || 0);
  });
  const preview = sorted.slice(0, 3);

  return (
    <section
      data-testid="live-events-preview"
      className="relative overflow-hidden py-24 md:py-32"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.08] grayscale"
        style={{ backgroundImage: `url(${BG})` }}
      />
      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#FF5500] opacity-75 live-pulse" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF5500]" />
              </span>
              <span className="font-ui text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF5500]">
                Eventos en vivo
              </span>
            </div>
            <h2 className="max-w-xl font-heading text-3xl font-black leading-tight tracking-tighter text-white sm:text-4xl lg:text-5xl">
              Entra a la arena ahora mismo
            </h2>
          </div>
          <Link
            to="/eventos"
            data-testid="preview-ver-todos"
            className="group inline-flex items-center gap-2 self-start border border-white/15 px-6 py-3.5 font-ui text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-200 hover:border-[#FF5500] hover:text-[#FF5500] md:self-auto"
          >
            Ver todos los eventos
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>

        {preview.length === 0 ? (
          <div
            data-testid="preview-empty"
            className="border border-white/10 bg-[#0F172A] p-16 text-center"
          >
            <p className="font-body text-sm text-[#94A3B8]">
              Cargando los últimos eventos de La Pinza…
            </p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {preview.map((e, i) => (
              <LiveEventCard
                key={e.id}
                event={e}
                index={i}
                participantCount={getCount(e)}
              />
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
